import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Monitor, Power } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../utils/cn';

// Inset focus ring — matches the nav rows; inset so the rail's clipping can't
// swallow it.
const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ' +
  'focus-visible:ring-primary-500 dark:focus-visible:ring-carbon-400';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/**
 * SidebarFooter — pinned footer of the navigation rail holding the colour-theme
 * selector (light / dark / system) and the sign-out action. Renders a compact
 * icon-only variant when the rail is collapsed.
 */
export default function SidebarFooter({ isCollapsed }) {
  const { theme, setTheme } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  const current = THEME_OPTIONS.find((o) => o.value === theme) ?? THEME_OPTIONS[2];

  // Collapsed: a single theme button that cycles through the options, plus a
  // sign-out icon. Both lean on native title/aria-label for their names.
  if (isCollapsed) {
    const cycleTheme = () => {
      const i = THEME_OPTIONS.findIndex((o) => o.value === theme);
      setTheme(THEME_OPTIONS[(i + 1) % THEME_OPTIONS.length].value);
    };
    const CurrentIcon = current.icon;
    return (
      <div className="border-t border-slate-200 dark:border-white/10 px-2 py-3 flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={cycleTheme}
          title={`Theme: ${current.label} (click to change)`}
          aria-label={`Theme: ${current.label}. Change theme`}
          className={cn(
            'w-10 h-10 flex items-center justify-center transition-colors',
            'text-slate-600 dark:text-white/70 hover:bg-slate-100 dark:hover:bg-white/[0.06]',
            'active:scale-95 motion-reduce:active:scale-100', FOCUS
          )}
        >
          <CurrentIcon size={18} />
        </button>
        <button
          type="button"
          onClick={handleLogout}
          title="Sign out"
          aria-label="Sign out"
          className={cn(
            'w-10 h-10 flex items-center justify-center transition-colors',
            'text-slate-500 hover:text-red-600 hover:bg-red-50',
            'dark:text-white/55 dark:hover:text-red-400 dark:hover:bg-red-900/20',
            'active:scale-95 motion-reduce:active:scale-100', FOCUS
          )}
        >
          <Power size={18} />
        </button>
      </div>
    );
  }

  // Expanded: the 3-tap theme toggle + a labelled logout merged into a single
  // bordered control bar — the iOS-style sliding-thumb theme track on the left,
  // the logout attached on the right with a column rule between them.
  const activeIndex = Math.max(0, THEME_OPTIONS.findIndex((o) => o.value === theme));
  return (
    <div className="border-t border-slate-200 dark:border-white/10 px-3 py-3">
      <div className="flex items-stretch border border-slate-300 dark:border-white/15">
      <div
        role="radiogroup"
        aria-label="Colour theme"
        className="relative flex-1 grid grid-cols-3 p-0.5 bg-slate-100 dark:bg-white/[0.04]"
      >
        {/* Sliding raised thumb — sits behind the active icon. */}
        <span
          aria-hidden
          style={{ transform: `translateX(calc(${activeIndex} * 100%))` }}
          className={cn(
            'pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc((100%-0.25rem)/3)]',
            'bg-white shadow-sm dark:bg-white/[0.10] dark:ring-1 dark:ring-white/10',
            'transition-transform duration-200 ease-out-expo motion-reduce:transition-none'
          )}
        />
        {THEME_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = theme === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={opt.label}
              title={opt.label}
              onClick={() => setTheme(opt.value)}
              className={cn(
                'relative z-10 h-8 flex items-center justify-center transition-colors', FOCUS,
                active
                  ? 'text-primary-600 dark:text-carbon-400'
                  : 'text-slate-500 dark:text-white/55 hover:text-slate-800 dark:hover:text-white/85'
              )}
            >
              <Icon
                size={16}
                className="flex-shrink-0 transition-transform active:scale-90 motion-reduce:active:scale-100"
              />
            </button>
          );
        })}
      </div>

        <button
          type="button"
          onClick={handleLogout}
          aria-label="Logout"
          className={cn(
            'flex items-center gap-2 px-3 flex-shrink-0 border-l border-slate-300 dark:border-white/15 transition-colors',
            'font-ledger-mono text-xs font-bold uppercase tracking-[0.1em]', FOCUS,
            'text-slate-600 hover:text-red-600 hover:bg-red-50',
            'dark:text-white/60 dark:hover:text-red-400 dark:hover:bg-red-900/20',
            'active:scale-95 motion-reduce:active:scale-100'
          )}
        >
          <Power size={16} className="flex-shrink-0" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}
